import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { IonicModule, ModalController, NavController, PopoverController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionDropdownComponent } from 'src/app/components/action-dropdown/action-dropdown.component';
import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { ComposePage } from 'src/app/components/compose/compose.page';
import { EditTagLinksComponent } from 'src/app/components/edit-tag-links/edit-tag-links.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import {
  INTEGRATIONS,
  MESSAGE_SOURCE_TYPE,
  PRIVATE_CHANNEL_NAME,
  PROJECT_MEMBER_ROLES,
  routes,
} from 'src/app/constants';
import { SelectedFilterMap } from 'src/app/utils/filter-utils';
import {
  createCustomerRecipient,
  downloadCSV,
  formatSlackChannelName,
  getWhereClausesFromFilters,
  removeUndefinedFields,
} from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { Customer, SlackChannel, WhereClause } from 'src/models';
import { CHANNEL_TYPES } from 'src/models/channels';
import { PROJECT_VISIBILITY } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';

import { EditEmailChannelComponent } from '../email/edit-email-channel.component';
import { EditMsTeamsChannelComponent } from '../ms-teams/edit-ms-teams-channel.component';
import { EditWidgetChannelComponent } from '../widget/edit-widget-channel.component';

const PATH_TO_CHANNEL_TYPE = {
  slack: MESSAGE_SOURCE_TYPE.SLACK,
  email: MESSAGE_SOURCE_TYPE.EMAIL,
  'microsoft-teams': MESSAGE_SOURCE_TYPE.TEAMS,
  'in-app-chat': MESSAGE_SOURCE_TYPE.WIDGET,
  jsm: MESSAGE_SOURCE_TYPE.JSM,
} as const;

@Component({
  standalone: true,
  imports: [
    SharedDirectivesModule,
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    AvatarModule,
    SharedPipesModule,
    NgxTippyModule,
    FilterButtonComponent,
    RouterModule,
  ],
  selector: 'app-channel-list',
  templateUrl: './channel-list.page.html',
  styleUrls: ['./channel-list.page.scss'],
})
export class ChannelListPage implements OnInit {
  filters: SelectedFilterMap = new Map();

  selectedChannels: Partial<Customer>[] = [];

  public readonly channels$: BehaviorSubject<Partial<Customer>[]> = new BehaviorSubject([]);
  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  routerSub: Subscription;
  connectedCrm: string;
  showArchived = false;
  limit = 60;
  cursor: any;
  sortField = 'name';
  sortDesc = false;
  selectAllPagesForBulkUpdate = false;
  channelType: string;
  validEmailDomains: any[];
  showCreateButton = false;

  PROJECT_VISIBILITY = PROJECT_VISIBILITY;
  CHANNEL_TYPES = CHANNEL_TYPES;
  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;

  assigneeLookups: any = {};

  filterOptions = [
    {
      label: 'Tag',
      id: 'tags.id',
      icon: 'pricetag',
      type: 'select',
      values: async () => {
        this.isLoading = true;

        const tags = await this.tagsService.list({
          limit: null,
          order: ['name'],
          descending: false,
        });

        const result = tags.data.map((tag) => {
          return {
            label: tag.name,
            id: tag.id,
          };
        });

        this.isLoading = false;

        return result;
      },
    },
    {
      label: 'Date created',
      id: 'createdAt',
      icon: 'calendar',
      type: 'date',
    },
    {
      label: 'Project',
      id: 'projectId',
      icon: 'custom-project',
      type: 'select',
    },
  ];

  select = [
    'id',
    'name',
    'replyTimeoutMinutes',
    'defaultTriageChannelId',
    'disableAutomatedTicketing',
    'botHandling',
    'autoresponder',
    'supportSteps',
    'slackTeamIds',
    'slackTeamNames',
    'assignToTaggedUserEnabled',
    'assignToFirstResponderEnabled',
    'createdAt',
    'updatedAt',
    'createdBy',
    'updatedBy',
    'slackChannel.id',
    'slackChannel.name',
    'slackChannel.isPrivate',
    'slackChannel.botIds',
    'isInternal',
    'externalCrmMetadata',
    'customerViewEnabled',
    'adminEmails',
    'slackEnabled',
    'emailDomains',
    'emailDomainsWrapper.emailDomains',
    'supportStepRuleset',
    'tags.name',
    'tags.id',
    'project.id',
    'project.name',
    'project.visibility',
    'type',
    'inboundEmailRule.id',
    'inboundEmailRule.email',
    'inboundEmailRule.slackChannel.id',
    'inboundEmailRule.slackChannel.name',
    'inboundEmailRule.slackChannel.isPrivate',
    'inboundEmailRule.slackChannel.botIds',
    'externalChatConnection.id',
    'externalChatConnection.slackChannel.id',
    'externalChatConnection.slackChannel.name',
    'externalChatConnection.slackChannel.isPrivate',
    'externalChatConnection.slackChannel.botIds',
    'widget.id',
    'widget.slackChannel.id',
    'widget.slackChannel.name',
    'widget.slackChannel.isPrivate',
    'widget.slackChannel.botIds',
    'jsmChannel.id',
    'jsmChannel.jsmInstance.id',
    'jsmChannel.jsmInstance.name',
    'jsmChannel.jsmServiceDesk.id',
    'jsmChannel.jsmServiceDesk.key',
    'jsmChannel.jsmServiceDesk.name',
  ];

  constructor(
    private customersService: CustomersService,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navController: NavController,
    private tagsService: TagsService,
    private analyticsService: AnalyticsService,
    private popoverCtrl: PopoverController,
    private location: Location,
    private searchablePopoverService: SearchablePopoverService,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.channelType = PATH_TO_CHANNEL_TYPE[this.router.url.split('/').pop() ?? ''] ?? 'slack';

    if (this.authService.tenant.integrations[INTEGRATIONS.salesforce.key]?.enabled) {
      this.connectedCrm = 'Salesforce';
    } else if (this.authService.tenant.integrations[INTEGRATIONS.hubspot.key]?.enabled) {
      this.connectedCrm = 'Hubspot';
    }

    this.showCreateButton =
      this.authService.isTenantAdmin || this.authService.projects.some((p) => p.role === PROJECT_MEMBER_ROLES.OWNER);

    this.loadChannels();

    if (this.authService.isTenantAdmin && this.channelType === MESSAGE_SOURCE_TYPE.EMAIL) {
      this.loadEmailDomains();
    }
  }

  async newEmailInbox() {
    const modal = await this.modalCtrl.create({
      component: EditEmailChannelComponent,
      cssClass: 'modal-medium',
    });
    modal.onDidDismiss().then(() => {
      this.loadChannels();
    });
    modal.present();
  }

  async newInAppChat() {
    const modal = await this.modalCtrl.create({
      component: EditWidgetChannelComponent,
      cssClass: 'modal-large',
    });
    modal.onDidDismiss().then(() => {
      this.loadChannels();
    });
    modal.present();
  }

  async newMicrosoftTeams() {
    const modal = await this.modalCtrl.create({
      component: EditMsTeamsChannelComponent,
      cssClass: 'modal-medium',
    });
    modal.onDidDismiss().then(() => {
      this.loadChannels();
    });
    modal.present();
  }

  fetchChannels = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<Customer>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      select: this.select,
    };

    const where: WhereClause[] = [];

    const filterWheres = getWhereClausesFromFilters(this.filters);

    where.push(...filterWheres);

    where.push({
      field: 'type',
      operator: '==',
      value: this.channelType,
    });

    if (!this.showArchived) {
      where.push({
        field: 'slackChannel.archivedAt',
        operator: '==',
        value: null,
      });
    }

    if (this.authService.selectedProject) {
      where.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    if (where.length) {
      options.where = where;
    }

    if (this.searchText) {
      options.search = this.searchText;
    }

    const result = await this.customersService.list(options);

    return result;
  };

  channelListDataSource = createDataSource(this.fetchChannels);

  async loadChannels() {
    this.isLoading = true;

    await this.channelListDataSource.firstPage();

    this.isLoading = false;
  }

  async reloadChannels() {
    this.isLoading = true;

    await this.channelListDataSource.reloadPage();

    this.isLoading = false;
  }

  async loadEmailDomains() {
    const emailDomainsResponse: any = await this.apiService.postPromise('/email-domains/list', {
      limit: null,
    });

    const domains = emailDomainsResponse.data ?? [];

    this.validEmailDomains = domains.filter((d) => d.valid);
  }

  showSLAOptions(channel: Customer) {
    this.searchablePopoverService.slas({
      event,
      selectedSlaId: channel.supportStepRuleset?.id,
      callback: (id: string, _title: string, sla: any) => {
        this.updateChannel(channel.id, {
          supportStepRulesetId: id,
        });
        channel.supportStepRuleset = sla;
      },
    });
  }

  onSelect({ selected }) {
    this.selectedChannels.splice(0, this.selectedChannels.length);
    this.selectedChannels.push(...selected);
    this.selectAllPagesForBulkUpdate = false;
  }

  getRowClass() {
    return 'border-bottom';
  }

  async composeEmail() {
    this.analyticsService.trackEvent('Channel List', 'Bulk Compose Email');

    const emails = this.selectedChannels.map((channel) => {
      return createCustomerRecipient(channel);
    });

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        emails,
        showTemplates: true,
      },
      cssClass: 'modal-large',
    });
    modal.present();

    this.selectedChannels = [];
  }

  composeSlack() {
    this.analyticsService.trackEvent('Channel List', 'Bulk Compose Slack');

    const channelIds = this.selectAllPagesForBulkUpdate
      ? 'all'
      : this.selectedChannels
          .map((channel) => {
            return channel.id;
          })
          .join(',');

    this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: removeUndefinedFields({
        customerIds: channelIds,
        previousUrl: this.location.path(),
      }),
    });

    this.selectedChannels = [];
  }

  openSlackChannel(slackChannelId: string) {
    window.open(`https://slack.com/app_redirect?channel=${slackChannelId}`);
  }

  openLink(url: string) {
    window.open(url, '_blank');
  }

  showNextPage() {
    this.pageCount += 1;
  }

  async manageTags() {
    const modal = await this.modalCtrl.create({
      component: EditTagLinksComponent,
      componentProps: {
        objectIds: this.selectedChannels.map((c) => c.id),
        addCallback: async (tagId, channelIds) => {
          await this.tagsService.createCustomerLinks(tagId, channelIds);
          await this.reloadChannels();
        },
        removeCallback: async (tagId, channelIds) => {
          await this.tagsService.deleteCustomerLinks(tagId, channelIds);
          await this.reloadChannels();
        },
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.selectedChannels = [];
    });
  }

  async showEditOptions() {
    const options = [
      {
        label: 'Edit tags',
        icon: 'pricetags-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();

          switch (index) {
            case 0: {
              this.manageTags();

              break;
            }
            default:
              break;
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  handleFilters() {
    this.loadChannels();
  }

  async updateChannel(channelId: string, updates: Partial<Customer>) {
    try {
      await this.customersService.update(channelId, updates);

      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.msgSrvc.show('Something went wrong');
    }
  }

  getTagsForRow(row: any) {
    return row.tags?.map((tag) => tag.name).join(', ');
  }

  async editTagForChannel(channelId: string, tagId: string) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: [
          {
            label: 'Remove tag',
            icon: 'close-circle',
          },
        ],
        callback: async (index: number, _label: string) => {
          popover.dismiss();

          if (index === 0) {
            await this.tagsService.deleteCustomerLinks(tagId, [channelId]);
            this.loadChannels();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  addTagToChannel(channelId: string) {
    this.searchablePopoverService.tags({
      event,
      projectId: this.authService.selectedProject?.id,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Channel List', 'Add Tag From Row');

        await this.tagsService.createCustomerLinks(id, [channelId]);

        this.reloadChannels();
      },
    });
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.loadChannels();
  }

  async exportAsCsv() {
    this.analyticsService.trackEvent('Channel List', 'Export CSV');

    const date = new Date();

    const channels = await this.fetchChannels({
      limit: null,
    });

    const results = channels.data.map((c) => {
      return {
        ...c,
        slackTeamIds: c.slackTeams?.map((t) => t.id).join(','),
        slackTeamNames: c.slackTeams?.map((t) => t.name).join(','),
        slackChannelName: c.slackChannel?.name ?? '',
      };
    });

    const headers = [
      'name',
      'replyTimeoutMinutes',
      'defaultTriageChannelId',
      'disableAutomatedTicketing',
      'botHandling',
      'autoresponder',
      'supportSteps',
      'slackTeamIds',
      'slackTeamNames',
      'assignToTaggedUserEnabled',
      'assignToFirstResponderEnabled',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
      'slackChannelId',
      'slackChannelName',
      'isInternal',
      'customerViewEnabled',
      'slackEnabled',
      'emailDomains',
      'id',
      'supportStepRuleset',
      'tags',
    ];

    downloadCSV(results, headers, `channel-export-${date.getTime()}`);
  }

  formatSlackChannelName(slackChannel: SlackChannel) {
    return formatSlackChannelName(slackChannel);
  }
}
